import { Browser } from '../../legacy'
import withRetry from 'aria-viewer/utils/retry'
import Socket from 'aria-viewer/analytics/utils/socket/socket'
import { _btoa } from './analyticUtils'

/**
 *	Custom analytics class which utilizes AWS Kinesis service for the
 *	collection of analytics data to be pushed up a data stream.
 */
class KinesisAnalytics {
    static analyticsData = {
        isReady: false,
        unTrackedEvents: [],
        _onReadyCB: undefined,
        _onCloseCB: undefined,
        _onMessageCB: undefined,
        browserData: undefined,
        socket: undefined,
    }

    /**
     * Construct Kinesis instance.
     *
     * @param  string analyticsToken The analytics token.
     * @param  Object params         Optional params which may include onReady, onMessage, onClose and onIntialized callback.
     */
    static init(analyticsToken, params = {}) {
        // Bind callback events
        data.onReady = params.onReady
        data.onMessage = params.onMessage
        data.onClose = params.onClose
        data.onIntialized = params.onIntialized

        data.unTrackedEvents = []

        data.browserData = KinesisAnalytics._getBrowserData()

        const hasSocketParam = params.socket !== undefined

        if (!hasSocketParam) {
            const webSocketEndpoint = params.endpoint
            const socket = new Socket(webSocketEndpoint)

            KinesisAnalytics._setupSocket(socket)
        } else {
            const delay = params.delay || 250
            const retries = params.retries || 3
            const backOffCoefficient = params.backOffCoefficient || 0.75

            withRetry({
                retries,
                fn: _ => {
                    return new Promise((resolve, reject) => {
                        if (!params.socket.isReady) reject()
                        else resolve()
                    })
                },
                delay,
                backOffCoefficient,
                onRetry: _ => console.log('Retrying to establish web socket from parent!'),
                onFailed: _ => {
                    // Fall back to using WebSockets
                    const webSocketEndpoint = params.endpoint
                    const socket = new Socket(webSocketEndpoint)

                    KinesisAnalytics._setupSocket(socket)
                },
                onSuccess: _ => {
                    const socket = params.socket

                    KinesisAnalytics._setupSocket(socket)
                },
            })
        }
    }

    /**
     * Handle notifying client once Socket connection is ready.
     *
     * @param  function cb Callback function to notify of readiness.
     */
    static onReady(cb) {
        if (typeof cb == 'function') data._onReadyCB = cb
    }

    /**
     * Handle notifying client once Socket connection has closed.
     *
     * @param  function cb Callback function to notify of socket closure.
     */

    static onClose(cb) {
        if (typeof cb == 'function') data._onCloseCB = cb
    }

    /**
     * Handle notifying client once Socket has recieved a message.
     *
     * @param  function cb Callback function to notify of socket communication.
     */

    static onMessage(cb) {
        if (typeof cb == 'function') data._onMessageCB = cb
    }

    /**
     * The event and/or properties to have tracked.
     *
     * @param  string event      The event name to track.
     * @param  object properties optional properties to track.
     */
    static track(event, properties) {
        const kinesisProperties = {
            ...data.browserData,
            ...properties,
        }

        const analyticsData = {
            event,
            properties: kinesisProperties,
        }

        if (!data.isReady) return KinesisAnalytics._batchEvents([analyticsData])

        const socketData = KinesisAnalytics._formatSocketAction(analyticsData)

        data.socket.send(socketData)
    }

    /**
     * An array of events to have tracked.
     *
     * @param  array events The array of events to track.
     */
    static trackBatch(events) {
        if (!data.isReady) return KinesisAnalytics._batchEvents(events)

        const socketData = KinesisAnalytics._formatSocketAction(events)

        data.socket.send(socketData)
    }

    /**
     * Closes WebSocket analytics
     */
    static close() {
        data.socket.close()
    }

    /**
     *	Temporary place holder for events to track prior to
     *	KinesisAnalytics being ready.
     *
     * @param  array events The array of events to track.
     */
    static _batchEvents(events) {
        data.unTrackedEvents = [...data.unTrackedEvents, ...events]
    }

    /**
     * Parses out client based browser data and returns as an object.
     *
     * @return object Clients drowser data.
     */
    static _getBrowserData() {
        const { browser, version, os } = Browser.getBrowserData()
        const {
            screen: { width, height },
            location: { href },
        } = window
        const { referrer } = document

        return {
            Browser: browser,
            BrowserVersion: version,
            // Nice to have City and Country. Would require making an api call.
            // City: '',
            // Country: '',
            Current_URL: href,
            Referrer: referrer,
            OS: os,
            Screen_Height: height,
            Screen_Width: width,
        }
    }

    /**
     * Sets up the Socket callbacks
     *
     * @param {*} socket a Socket instance
     */
    static _setupSocket(socket) {
        data.socket = socket

        socket.onopen = event => {
            data.isReady = true

            // If ready callback has been provided then notify.
            ;(data.onReady && data.onReady(event)) || (data._onReadyCB && data._onReadyCB(event))

            socket.onmessage = function(event) {
                ;(data.onMessage && data.onMessage(event)) || (data._onMessageCB && data._onMessageCB(event))
            }

            socket.onclose = function(event) {
                ;(data.onClose && data.onClose(event)) || (data._onCloseCB && data._onCloseCB(event))
            }

            // Process any untracked events.
            if (data.unTrackedEvents.length > 0) {
                KinesisAnalytics.trackBatch(data.unTrackedEvents)
                data.unTrackedEvents = []
            }
        }

        // Done running the intialized function
        data.onIntialized && data.onIntialized()
    }

    /**
     * Stringifies the analytics data object.
     *
     * @param array | {} analyticsData the analytics data to send for processing
     */
    static _formatSocketAction(analyticsData) {
        return JSON.stringify({
            action: 'trackAnalytics',
            data: _btoa(
                JSON.stringify({
                    eventType: 'track',
                    eventPayload: analyticsData,
                })
            ),
        })
    }
}

const data = KinesisAnalytics.analyticsData

export default KinesisAnalytics
